import dayjs from 'dayjs';
import { sortBy } from 'lodash';

import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';

// Move these out of getTemplateValues to make accessible in other utils
// Get the query string from the current URL
const queryString = window.location.search;

// Create a new URLSearchParams object
const urlParams = new URLSearchParams(queryString);

// Get start and end dates
const startDate = dayjs(urlParams.get('start'));
const endDate = dayjs(urlParams.get('end'));

export const getTemplateValues = () => {
  const diff = endDate.diff(startDate, 'day');
  // Ensure that the difference is at least 1 day
  if (diff === 0) {
    return 1;
  }

  // Add 1 to be inclusive
  return diff + 1;
};

export const formatDateRange = (frequencyName: string | null) => {
  if (!startDate || !endDate || !frequencyName) {
    return '';
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  switch (frequencyName.toLowerCase()) {
    case 'daily':
      return `Day: ${start.format('LL')}`;

    case 'weekly':
      return `Week: ${start.format('LL')} - ${end.format('LL')}`;
    case 'custom':
      return `Custom: ${start.format('LL')} - ${end.format('LL')}`;
    case 'quarterly':
      return `Quarter: ${start.format('LL')} - ${end.format('LL')}`;

    case 'monthly':
      return `Month: ${start.format('MMMM YYYY')}`;

    case 'annually': {
      return `Year: ${start.format('YYYY')}`;
    }

    default:
      return `Custom: ${start.format('LL')} - ${end.format('LL')}`;
  }
};

export const ticketColumns: any = [
  {
    field: 'status',
    flex: 1,
    renderCell: () => <div>column 1</div>,
    align: 'right'
  },
  {
    field: 'priority',
    renderCell: () => <div>column 2</div>,
    align: 'right'
  }
];

export const sortAndTruncateData = (
  data: any,
  limit: number,
  sortByField: string
) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  const sortedData = sortBy(data, [sortByField]).reverse();
  const truncatedData = sortedData.slice(0, limit);
  return truncatedData;
};

export function interpolateColorHex(
  startHex: string,
  endHex: string,
  factor: number
): string {
  const parseHex = (hex: string) => ({
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16)
  });

  const toHex = (val: number) => val.toString(16).padStart(2, '0');

  const start = parseHex(startHex);
  const end = parseHex(endHex);

  const r = Math.round(start.r + (end.r - start.r) * factor);
  const g = Math.round(start.g + (end.g - start.g) * factor);
  const b = Math.round(start.b + (end.b - start.b) * factor);

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export const ceilWithZero = (num: number) => {
  return num === 0 ? 0 : Math.ceil(num);
};

export const getURLTimeframeData = (data: any[], dateType: string) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const filteredData = data.filter((item) => {
    const itemDate = dayjs(item[dateType]);

    return (
      itemDate.isSameOrAfter(dayjs(startDate), 'day') &&
      itemDate.isSameOrBefore(dayjs(endDate), 'day')
    );
  });

  return filteredData;
};

export const getCountsByDate = (
  timeFrame: number,
  data: any[],
  reportEndDate: string
) => {
  const sortedData = data.sort((a, b) => {
    return dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1;
  });

  // Ensure every date in the timeFrame is represented, even if there are no items for that date
  const countsByDate: Record<string, number> = {};
  for (let i = 0; i <= timeFrame - 1; i += 1) {
    const date = `${dayjs(reportEndDate)
      .subtract(timeFrame - 1 - i, 'days')
      .format(YEAR_MONTH_DAY_FORMAT)}`;
    countsByDate[date] = 0;
  }
  sortedData.forEach((item) => {
    const { date } = item;
    countsByDate[date] = (countsByDate[date] || 0) + (item.count || 0);
  });

  return countsByDate;
};

export const getGraphCounts = (
  timeFrame: number,
  data: any[],
  reportEndDate: string
) => {
  // Create a map for quick lookup of existing data by date
  const dataMap = new Map<string, any>();

  data.forEach((item) => {
    const dateKey = dayjs(item.value).format(YEAR_MONTH_DAY_FORMAT);
    const existingItem = dataMap.get(dateKey);

    if (existingItem) {
      // If date already exists, accumulate the count (handles multiple alert types per day)
      dataMap.set(dateKey, {
        ...existingItem,
        count: (existingItem.count || 0) + (item.count || 0)
      });
    } else {
      // First time seeing this date, set it
      dataMap.set(dateKey, item);
    }
  });

  // Generate all dates in the timeframe with their data
  const allDatesData = [];
  for (let i = 0; i <= timeFrame - 1; i += 1) {
    const date = dayjs(reportEndDate)
      .subtract(timeFrame - 1 - i, 'days')
      .format(YEAR_MONTH_DAY_FORMAT);

    const existingData = dataMap.get(date);

    if (existingData) {
      // Push all data from the existing item, but ensure date is a Date object
      allDatesData.push({
        ...existingData,
        date: dayjs(date).toDate()
      });
    } else {
      // Create a default entry for dates with no data
      allDatesData.push({
        date: dayjs(date).toDate(),
        count: 0
      });
    }
  }

  return allDatesData;
};

export default getTemplateValues;
